import { useCallback, useEffect, useRef, useState } from 'react';
import connectionHubManager from 'connectionHub/connectionHubManager';
import { EJobStatus } from 'business/models/CustomModels';
import type { ProgressStatus } from 'components/modal/ProgressModal';

export type { ProgressStatus };

export interface ProgressState {
  show: boolean;
  processed: number;
  total: number;
  status: ProgressStatus;
  errorMessage?: string;
}

const INITIAL_STATE: ProgressState = {
  show: false,
  processed: 0,
  total: 0,
  status: 'running',
};

const STATUS_MAP: Record<number, ProgressStatus> = {
  [EJobStatus.Completed]: 'completed',
  [EJobStatus.Error]: 'error',
};

const useProgressHub = (jobType: number) => {
  const [progress, setProgress] = useState<ProgressState>(INITIAL_STATE);
  const jobIdRef = useRef<string | null>(null);

  const setJobId = useCallback((id: string | null) => {
    jobIdRef.current = id;
  }, []);

  const resetProgress = useCallback(() => {
    setProgress(INITIAL_STATE);
    jobIdRef.current = null;
  }, []);

  useEffect(() => {
    let conn;
    let handler;
    let cancelled = false;

    (async () => {
      try {
        conn = await connectionHubManager.getConnection('progresshub', '/progressHub');
        if (cancelled) {
          return;
        }

        handler = (data) => {
          if (!data || data.jobType !== jobType) {
            return;
          }
          if (String(data.jobId) !== jobIdRef.current) {
            return;
          }

          const dataObj = data.dataObj ?? {};
          const status = STATUS_MAP[data.jobStatus] ?? 'running';

          setProgress({
            show: true,
            processed: typeof dataObj.processed === 'number' ? dataObj.processed : 0,
            total: typeof dataObj.total === 'number' ? dataObj.total : 0,
            status,
            errorMessage:
              status === 'error' ? dataObj.errorMessage ?? 'Update failed.' : undefined,
          });
        };

        conn.on('UpdateJobProgress', handler);
      } catch (e) {
        console.error('Failed to subscribe to progresshub:', e);
      }
    })();

    return () => {
      cancelled = true;
      if (conn && handler) {
        conn.off('UpdateJobProgress', handler);
      }
    };
  }, [jobType]);

  return { progress, setProgress, setJobId, resetProgress };
};

export default useProgressHub;
